import {
  BaseDateQuestion,
  BaseFormQuestion,
  BaseMultipleSelect,
  BaseRankingQuestion,
  BaseSingleSelect,
  isCalendar,
  isRecording,
  isCalendly,
  isScheduling,
  isForm,
  isMultipleSelect,
  isRankingQuestion,
  isSingleSelect,
} from '@hubertai/hubert-component-library';
import type {
  TAnyClientQuestionType,
  TCalendlyQuestion,
  TSchedulingQuestion,
  TRecordingQuestion,
  TAvailableLanguageCodes,
  TCalendarQuestion,
  TFormQuestion,
} from '@hubertai/screening-question-types';
import { Clock } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingPreview } from './BookingPreview';
import { RecordingPreview } from './RecordingPreview';

interface PreviewQuestionProps extends ComponentProps<'div'> {
  questionConfig: TAnyClientQuestionType;
  language: TAvailableLanguageCodes;
}

const previewBody = (questionConfig: TAnyClientQuestionType, language: TAvailableLanguageCodes) => {
  if (isSingleSelect(questionConfig)) {
    return <BaseSingleSelect rawQuestion={questionConfig} isPreview={true} language={language} onSend={() => {}} />;
  }

  if (isMultipleSelect(questionConfig)) {
    return <BaseMultipleSelect rawQuestion={questionConfig} isPreview={true} language={language} onSend={() => {}} />;
  }

  if (isRankingQuestion(questionConfig)) {
    return (
      <BaseRankingQuestion
        mode="click"
        rawQuestion={questionConfig}
        language={language}
        onChange={() => {}}
        isPreview={false}
      />
    );
  }

  if (isForm(questionConfig)) {
    // Element types are migrated into the library in waves; the ones that are not there yet
    // show as labelled placeholders, so the recruiter still sees the shape of the form.
    return (
      <BaseFormQuestion
        rawQuestion={questionConfig as unknown as TFormQuestion<string>}
        language={language}
        isPreview={true}
      />
    );
  }

  if (isCalendar(questionConfig)) {
    return (
      <BaseDateQuestion
        rawQuestion={questionConfig as unknown as TCalendarQuestion<string>}
        language={language}
        isPreview={true}
      />
    );
  }

  if (isRecording(questionConfig)) {
    // A capability card, not a recorder — see RecordingPreview for why.
    return <RecordingPreview question={questionConfig as unknown as TRecordingQuestion<string>} />;
  }

  if (isScheduling(questionConfig) || isCalendly(questionConfig)) {
    // A capability card, not a live booking widget — see BookingPreview for why.
    return (
      <BookingPreview
        question={questionConfig as unknown as TSchedulingQuestion<string> | TCalendlyQuestion<string>}
        language={language}
      />
    );
  }

  return null;
};

const PreviewQuestion = ({ questionConfig, language, ...props }: PreviewQuestionProps) => {
  const { t } = useTranslation('Questionsv2');
  const preview = previewBody(questionConfig, language);
  if (!preview && !questionConfig.deferrable) return null;

  return (
    <div {...props}>
      {preview}
      {questionConfig.deferrable ? (
        <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
          <Clock size={11} />
          {t('deferrable.label')}
        </span>
      ) : null}
    </div>
  );
};

export default PreviewQuestion;
